import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = "/api";

/**
 * Admin-only page to view & override the TRY→PHP exchange rate used when
 * charging Turkish-priced packages through PayMongo (which only supports PHP).
 *
 * Auto-rate is fetched hourly by the backend scheduler from open.er-api.com.
 * Admin can override with a manual rate (e.g. to add a small FX margin).
 */
export default function AdminFxRate() {
  const { getAuthHeader } = useAuth();
  const headers = { headers: getAuthHeader() };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const [manualRate, setManualRate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payments/paymongo/fx-rate`);
      setData(r.data);
      setManualOverride(!!r.data.manual_override);
      setManualRate(r.data.manual_rate ? String(r.data.manual_rate) : "");
    } catch (e) {
      toast.error("Failed to load FX rate");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (manualOverride && (!manualRate || parseFloat(manualRate) <= 0)) {
      toast.error("Please enter a positive rate");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/payments/paymongo/admin/fx-rate`,
        {
          manual_override: manualOverride,
          manual_rate: manualOverride ? parseFloat(manualRate) : null,
        },
        headers
      );
      setData(r.data);
      toast.success(
        manualOverride ? "Manual rate saved" : "Reverted to auto-fetched rate"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(
        `${API}/payments/paymongo/admin/fx-rate/refresh`,
        {},
        headers
      );
      setData(r.data);
      toast.success(`Live rate refreshed: 1 TRY = ${r.data.rate.toFixed(4)} PHP`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const effectiveRate = data?.rate || 0;
  const fmt = (n) => (typeof n === "number" ? n.toFixed(4) : n || "—");
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="admin-fx-title">
          TRY → PHP Exchange Rate
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Controls how much your students are charged in PHP when they pay
          through PayMongo. Auto-refreshed every hour from open.er-api.com.
        </p>
      </div>

      {/* Current effective rate */}
      <Card className="border-2 border-emerald-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Currently in use
            </p>
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700"
              data-testid="admin-fx-source"
            >
              {data?.source === "manual_override" ? "Manual" :
               data?.source === "auto_fetched" ? "Auto-fetched" : "Default"}
            </span>
          </div>
          <p className="text-4xl font-bold text-emerald-700" data-testid="admin-fx-effective">
            1 TRY = ₱{fmt(effectiveRate)}
          </p>
          <p className="text-sm text-slate-500 mt-2">
            Example: a 880 TL package will be charged{" "}
            <strong className="text-slate-900">₱{(880 * effectiveRate).toFixed(2)}</strong>{" "}
            on PayMongo.
          </p>
        </CardContent>
      </Card>

      {/* Auto-fetched info */}
      <Card>
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">Auto-fetched rate</p>
              <p className="text-xs text-slate-500">
                Source: open.er-api.com · Updates hourly
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="admin-fx-refresh"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh now
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-xs">Latest fetched</p>
              <p className="font-semibold" data-testid="admin-fx-auto-rate">
                1 TRY = ₱{fmt(data?.auto_rate)}
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Fetched at</p>
              <p className="font-semibold">{fmtDate(data?.auto_rate_fetched_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual override */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">Manual override</p>
              <p className="text-xs text-slate-500">
                Override the auto-fetched rate (e.g. to add a small FX margin)
              </p>
            </div>
            <Switch
              checked={manualOverride}
              onCheckedChange={setManualOverride}
              data-testid="admin-fx-manual-toggle"
            />
          </div>

          {manualOverride && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Manual rate (1 TRY = ? PHP)
              </label>
              <Input
                type="number"
                step="0.0001"
                min="0.0001"
                value={manualRate}
                onChange={(e) => setManualRate(e.target.value)}
                placeholder="e.g. 1.35"
                data-testid="admin-fx-manual-input"
              />
              {manualRate && parseFloat(manualRate) > 0 && (
                <p className="text-xs text-slate-500">
                  Preview: a 880 TL package will be charged{" "}
                  <strong>₱{(880 * parseFloat(manualRate)).toFixed(2)}</strong>
                </p>
              )}
            </div>
          )}

          {manualOverride && data?.auto_rate && manualRate && parseFloat(manualRate) > 0 && (
            <div className="text-xs flex items-start gap-1.5 pt-1">
              {Math.abs(parseFloat(manualRate) - data.auto_rate) / data.auto_rate > 0.1 ? (
                <>
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-amber-700">
                    Your rate differs from the live rate by more than 10%
                    (live: {fmt(data.auto_rate)}). Are you sure?
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">
                    Within 10% of the live rate ({fmt(data.auto_rate)}).
                  </span>
                </>
              )}
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="admin-fx-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
